import {
  INotification,
  INotificationActionContext,
  INotificationActionHandler,
  INotificationClickOptions,
  NavigateFn,
} from 'services/notifications/types'

const normalizeKey = (value?: string | null) => (value ?? '').trim().toLowerCase()

interface INotificationActionDispatcherOptions {
  handlers: INotificationActionHandler[]
  fallbackHandler: INotificationActionHandler
  normalize: (notification: INotification) => INotification
  markSeen?: (notification: INotification) => Promise<unknown> | unknown
  logger?: Pick<Console, 'warn'>
  warnOnDuplicateEvents?: boolean
}

export class NotificationActionDispatcher {
  private readonly serviceHandlers = new Map<string, INotificationActionHandler[]>()
  private readonly fallbackHandler: INotificationActionHandler
  private readonly normalize: (notification: INotification) => INotification
  private readonly markSeen?: (notification: INotification) => Promise<unknown> | unknown
  private readonly logger: Pick<Console, 'warn'>
  private readonly warnOnDuplicateEvents: boolean

  constructor({
    handlers,
    fallbackHandler,
    normalize,
    markSeen,
    logger = console,
    warnOnDuplicateEvents = false,
  }: INotificationActionDispatcherOptions) {
    this.fallbackHandler = fallbackHandler
    this.normalize = normalize
    this.markSeen = markSeen
    this.logger = logger
    this.warnOnDuplicateEvents = warnOnDuplicateEvents
    handlers.forEach((handler) => this.register(handler))
  }

  register(handler: INotificationActionHandler) {
    const serviceKey = normalizeKey(handler.service)
    const existingServiceHandlers = this.serviceHandlers.get(serviceKey) ?? []
    this.serviceHandlers.set(serviceKey, [...existingServiceHandlers, handler])

    handler.eventTypes?.forEach((eventType) => {
      const eventKey = normalizeKey(eventType)
      const duplicateInService = existingServiceHandlers.some((existingHandler) =>
        existingHandler.eventTypes?.some((existingEventType) => normalizeKey(existingEventType) === eventKey)
      )

      if (this.warnOnDuplicateEvents && duplicateInService) {
        this.logger.warn(`[Notifications] duplicate event_type handler registered: ${serviceKey}.${eventKey}`)
      }
    })
  }

  getHandler(notification: INotification): INotificationActionHandler {
    const handlers = this.serviceHandlers.get(normalizeKey(notification.service))
    if (!handlers?.length) return this.fallbackHandler

    const eventKey = normalizeKey(notification.event_type)
    const eventHandler = handlers.find((handler) =>
      handler.eventTypes?.some((eventType) => normalizeKey(eventType) === eventKey)
    )

    return eventHandler ?? handlers.find((handler) => !handler.eventTypes?.length) ?? handlers[0]
  }

  getHandlerForService(service: string): INotificationActionHandler {
    return this.serviceHandlers.get(normalizeKey(service))?.[0] ?? this.fallbackHandler
  }

  async handle(
    notification: INotification,
    navigate: NavigateFn,
    options: INotificationClickOptions = {}
  ): Promise<void> {
    const normalized = this.normalize(notification)
    const handler = this.getHandler(normalized)

    const context: INotificationActionContext = { dispatch: options.dispatch }

    const beforeNavigate = handler
      .onBeforeNavigate?.(normalized, context)
      .catch((err) => this.logger.warn('[Notifications] onBeforeNavigate failed:', err))

    const destination = context.destination ?? (await handler.resolve(normalized, context))
    if (!destination.handled && destination.path) {
      navigate(destination.path, { replace: destination.replace ?? true, state: destination.state })
    }

    await beforeNavigate

    const markSeen = options.markSeen ?? this.markSeen
    if (!normalized.seen) {
      Promise.resolve(markSeen?.(normalized)).catch((err) => this.logger.warn('[Notifications] mark-seen failed:', err))
    }
  }
}
